import { useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import cx from 'classnames';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { BottomPanelKey } from 'types';
import {
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_POSTER_SIZE_LIMIT,
  EVENT_POSTER_SUPPORTED_FORMATS,
  EVENT_TITLE_MAX_LENGTH
} from 'utils';
import {
  authSelector,
  hasMembershipPriceSelector,
  isMembersOnlyAvailableSelector,
  isRecordingAvailableSelector
} from 'store/selectors';
import { api, LoggerService, tracker } from 'services';
import { useBottomSheet, useRouter, useStore, useToast } from 'hooks';
import { BottomPanels, ImageUpload } from 'containers';
import { Calendar, Input, Loader, Switch, TextArea, TimePicker } from 'components';
import { CalendarIcon, ClockIcon, RecordingIcon, UserGroupIcon } from 'components/Icon';

import styles from './SchedulePanel.module.scss';

export type SchedulePanelProps = {
  onSubmitted?(channelName: string, eventCode: string): void;
};

export type FormValues = {
  topic: string;
  description: string;
  startDate: Date;
  poster: File | null;
  isMembersOnly: boolean;
  isRecordingEnabled: boolean;
};

export const validationSchema = yup.object().shape({
  topic: yup
    .string()
    .max(EVENT_TITLE_MAX_LENGTH, `The topic must be less than ${EVENT_TITLE_MAX_LENGTH} characters`)
    .required('The topic is required'),
  isMembersOnly: yup.boolean().required(),
  isRecordingEnabled: yup.boolean().required(),
  description: yup
    .string()
    .max(EVENT_DESCRIPTION_MAX_LENGTH, `must be less than ${EVENT_DESCRIPTION_MAX_LENGTH} characters`),
  startDate: yup
    .date()
    .min(dayjs().add(dayjs.duration(10, 'minutes')).toDate(), 'Start time must be at least 10 minutes later')
    .required(),
  poster: yup
    .mixed()
    .test(
      'fileSize',
      `File size must be less than ${EVENT_POSTER_SIZE_LIMIT / 1024}Kb`,
      (file: File) => !file || file.size <= EVENT_POSTER_SIZE_LIMIT
    )
    .test(
      'fileFormat',
      `Supported formats: ${EVENT_POSTER_SUPPORTED_FORMATS.join(', ')}`,
      (file: File) => !file || EVENT_POSTER_SUPPORTED_FORMATS.map(ext => `image/${ext}`).includes(file?.type)
    )
});

const SchedulePanel = ({ onSubmitted }: SchedulePanelProps): JSX.Element => {
  const toast = useToast();
  const router = useRouter();
  const selectedStartDate = useRef<Date>(dayjs().add(dayjs.duration(10, 'minutes')).toDate());

  const { auth, isMembersOnlyAvailable, isRecordingAvailable, hasMembershipPrice } = useStore(state => ({
    auth: authSelector(state),
    isMembersOnlyAvailable: isMembersOnlyAvailableSelector(state),
    isRecordingAvailable: isRecordingAvailableSelector(state),
    hasMembershipPrice: hasMembershipPriceSelector(state)
  }));
  const isMembersOnlyToggleActive = isMembersOnlyAvailable && hasMembershipPrice;
  const isRecordingToggleActive = isRecordingAvailable && hasMembershipPrice;

  const bottomSheet = useBottomSheet<BottomPanelKey.CHOOSE_DATE | BottomPanelKey.CHOOSE_TIME>();
  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      topic: '',
      description: '',
      startDate: selectedStartDate.current,
      poster: null,
      isMembersOnly: false,
      isRecordingEnabled: isRecordingAvailable
    }
  });

  const onSubmit = async (values: FormValues) => {
    const { name, image: avatar } = auth;
    const { topic, startDate, isMembersOnly, isRecordingEnabled, description, poster } = values;

    try {
      tracker.scheduleEvent();

      const { data } = await api.requests.ltv.createEvent({
        eventName: topic,
        hostName: name,
        hostAvatar: avatar,
        startDate: startDate.toISOString(),
        membersOnly: isMembersOnly,
        enableRecording: isRecordingEnabled,
        description,
        poster: poster || undefined
      });

      tracker.scheduleEventSuccess(data.code, isMembersOnly);

      onSubmitted?.(data.channelName, data.code);
    } catch (error) {
      LoggerService.log('SUBMIT_SCHEDULE_EVENT_FORM', error);

      if (error instanceof Error) {
        tracker.scheduleEventFail(error);
        toast.error(error?.message, { id: 'SCHEDULE_EVENT' });
      }
    }
  };

  const openDatePanel = () => bottomSheet.open(BottomPanelKey.CHOOSE_DATE);

  const openTimePanel = () => bottomSheet.open(BottomPanelKey.CHOOSE_TIME);

  const closePanel = () => bottomSheet.close();

  const onEnablePremiumForMembersOnly = () => {
    if (!isMembersOnlyAvailable) {
      router.push('/pricing', { membersOnly: true });

      return;
    }

    if (!hasMembershipPrice) {
      router.push('/get-paid');
    }
  };

  const onEnablePremiumForRecording = () => {
    if (!isRecordingAvailable) {
      router.push('/pricing', { recording: true });

      return;
    }

    if (!hasMembershipPrice) {
      router.push('/get-paid');
    }
  };

  const handleOnCancelDateChanges = () => {
    closePanel();
  };

  const handleOnSaveDateChanges = () => {
    form.setValue('startDate', selectedStartDate.current, { shouldValidate: true });
    closePanel();
  };

  const handleOnCancelTimeChanges = () => {
    closePanel();
  };

  const handleOnSaveTimeChanges = () => {
    form.setValue('startDate', selectedStartDate.current, { shouldValidate: true });
    closePanel();
  };

  const startDate = form.watch('startDate');

  return (
    <>
      <div className={styles.container}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className={styles.content}>
            {form.formState.isSubmitting && <Loader />}

            <div className="container">
              <Controller
                name="poster"
                defaultValue={null}
                control={form.control}
                render={({ field: { value, onChange } }) => (
                  <ImageUpload
                    maxSize={EVENT_POSTER_SIZE_LIMIT}
                    className={styles.poster}
                    invalid={!!form.formState.errors.poster}
                    feedback={form.formState.errors.poster?.message}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />

              <Controller
                name="topic"
                control={form.control}
                render={({ field }) => (
                  <Input
                    className={styles.topic}
                    invalid={!!form.formState.errors.topic}
                    placeholder="Add a Topic"
                    maxLength={EVENT_TITLE_MAX_LENGTH}
                    feedback={form.formState.errors.topic?.message}
                    {...field}
                  />
                )}
              />

              <div className={cx(styles.pickers, { [styles.invalid]: !!form.formState.errors.startDate })}>
                <div className={styles.content}>
                  <div className={styles.picker} aria-hidden="true" onClick={openDatePanel}>
                    <p className={styles.title}>Date</p>
                    <div className={styles.value}>
                      <span>{dayjs(startDate).format('MMM DD, YYYY')}</span>
                      <CalendarIcon />
                    </div>
                  </div>
                  <hr className={styles.separator} />
                  <div className={styles.picker} aria-hidden="true" onClick={openTimePanel}>
                    <p className={styles.title}>Time</p>
                    <div className={styles.value}>
                      <span>{dayjs(startDate).format('hh:mm A')}</span>
                      <ClockIcon />
                    </div>
                  </div>
                </div>
                {form.formState.errors.startDate && (
                  <span className={styles.feedback}>{form.formState.errors.startDate.message}</span>
                )}
              </div>

              <ul className={styles.toggles}>
                <li>
                  <Controller
                    control={form.control}
                    name="isMembersOnly"
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        className={styles.toggle}
                        icon={<UserGroupIcon />}
                        title="Members Only"
                        checked={value}
                        badge={!isMembersOnlyAvailable ? 'Premium' : ''}
                        disabled={!isMembersOnlyToggleActive}
                        onChange={onChange}
                        onClick={onEnablePremiumForMembersOnly}
                      />
                    )}
                  />
                </li>
                <li>
                  <Controller
                    control={form.control}
                    name="isRecordingEnabled"
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        className={styles.toggle}
                        icon={<RecordingIcon />}
                        title="Recording"
                        checked={value}
                        badge={!isRecordingAvailable ? 'Premium' : ''}
                        disabled={!isRecordingToggleActive}
                        onChange={onChange}
                        onClick={onEnablePremiumForRecording}
                      />
                    )}
                  />
                </li>
              </ul>

              <Controller
                name="description"
                control={form.control}
                render={({ field }) => (
                  <TextArea
                    className={styles.description}
                    maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
                    placeholder="Description"
                    {...field}
                  />
                )}
              />

              <button type="submit">
                <CalendarIcon />
                <span>Schedule event</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      <BottomPanels
        inner
        panels={[
          {
            name: BottomPanelKey.CHOOSE_DATE,
            show: bottomSheet.state.contentKey === BottomPanelKey.CHOOSE_DATE,
            title: 'Set date',
            component: (
              <div className={styles.date}>
                <div className="mb-20">
                  <Controller
                    control={form.control}
                    name="startDate"
                    render={({ field: { value } }) => (
                      <Calendar
                        defaultValue={value}
                        onChange={newValue => {
                          const date = new Date(value);

                          date.setFullYear(newValue.getFullYear());
                          date.setMonth(newValue.getMonth());
                          date.setDate(newValue.getDate());

                          selectedStartDate.current = date;
                        }}
                      />
                    )}
                  />
                </div>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={cx(styles.action, styles.cancel)}
                    onClick={handleOnCancelDateChanges}
                  >
                    Cancel
                  </button>
                  <button type="button" className={cx(styles.action, styles.save)} onClick={handleOnSaveDateChanges}>
                    Done
                  </button>
                </div>
              </div>
            )
          },
          {
            name: BottomPanelKey.CHOOSE_TIME,
            show: bottomSheet.state.contentKey === BottomPanelKey.CHOOSE_TIME,
            title: 'Set time',
            component: (
              <div className={styles.time}>
                <Controller
                  control={form.control}
                  name="startDate"
                  render={({ field: { value } }) => (
                    <TimePicker
                      defaultHour={value.getHours()}
                      defaultMinute={value.getMinutes()}
                      onChange={(h, m) => {
                        const date = new Date(value);

                        date.setHours(h);
                        date.setMinutes(m);
                        date.setSeconds(0);

                        selectedStartDate.current = date;
                      }}
                    />
                  )}
                />
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={cx(styles.action, styles.cancel)}
                    onClick={handleOnCancelTimeChanges}
                  >
                    Cancel
                  </button>
                  <button type="button" className={cx(styles.action, styles.save)} onClick={handleOnSaveTimeChanges}>
                    Done
                  </button>
                </div>
              </div>
            )
          }
        ]}
        onClose={closePanel}
      />
    </>
  );
};

export default SchedulePanel;
